/**
 * FilterPanelController
 *
 * Shared controller for office list filter panels. Wires up the
 * standard cleantask filter UI pattern:
 *
 *   - Collapse/expand the filter panel (#filter-panel) from a toggle
 *     button (#toggle-filters-btn), with chevron rotation.
 *   - Close on outside click (ignoring picker popups that live outside
 *     the panel in the DOM) and on Escape.
 *   - Active-filter chip strip (#active-filter-chips) rendered by
 *     templates/office/components/_filter_chips.html — each chip's
 *     data-remove-filter="<key>" triggers single-filter removal and
 *     [data-clear-all-filters] triggers a full reset.
 *   - Reset button (#reset-filters-btn) inside the panel.
 *   - Generic field reset that covers every input/select/checkbox in
 *     the form, plus SearchPicker hooks for autocomplete-driven
 *     hidden inputs (customer, cleaner, …).
 *   - Custom-select visual sync after programmatic .value changes.
 *   - Idempotent: safe to re-run after hx-boost navigations (rebinds
 *     fresh per-element listeners, keeps a single document listener).
 *
 * Architecture — document-level listeners are installed ONCE per tab
 * and dispatch through a module-level registry of controllers keyed
 * by formId. This matters across hx-boost navigations: the old page's
 * controller stays in the registry but its form disappears from the
 * DOM, so the dispatcher finds the controller whose form is still
 * live and routes chip / outside-click / Escape events to it. Binding
 * the global listeners to a specific controller instance (the old
 * approach) would trap chip clicks from later pages on the first
 * page's `this`, causing the bug where chips on Visits were inert
 * until the user refreshed.
 *
 * URL is the source of truth; the form is a visual mirror. Pair this
 * with ``form-url-sync.js`` (already global) so history/back/forward
 * restores stay consistent.
 *
 * Contract with the page:
 *   - Form is identified by `formId`.
 *   - Toggle/panel/reset/chips use the fixed IDs above. A page that
 *     needs more than one filter panel should not use this controller.
 *   - Chip keys emitted by the server MUST match form field `name`
 *     attributes. Virtual composite keys (e.g. "date_range" → clears
 *     both date_from/date_to) are handled via the `virtualKeys` map.
 *   - SearchPicker instances managed by the page are passed via the
 *     `pickers` array so the controller can `.clear()` them during
 *     reset and single-filter removal.
 *
 * Per-instance state lives on DOM nodes (dataset flags) rather than on
 * the instance itself so re-instantiating after a boosted navigation
 * is a no-op for already-bound elements.
 */

// Module-level registry: formId → controller. Used by the single
// document-level dispatcher to route events to the controller whose
// form is currently in the DOM. Entries are never removed (cheap:
// one per module), so navigating back to a page the user visited
// earlier just reuses the existing entry.
const CONTROLLER_REGISTRY = new Map();

let DOC_LISTENERS_BOUND = false;

// Per-element idempotency tracked in JS memory, NOT as a DOM dataset
// attribute. This matters because HTMX takes outerHTML snapshots for
// its history cache on hx-boost navigations. A dataset flag would be
// serialized into that snapshot, then restored on back-navigation as
// a "still bound" marker even though the restored node is a brand
// new DOM element with no listeners attached → the button appears
// dead until a hard refresh. A WeakSet sidesteps this entirely: old
// (replaced) nodes drop out via GC, new nodes are absent → we rebind.
const BOUND_ELEMENTS = new WeakSet();

/** Return the controller whose form exists in the current DOM, if any. */
function findLiveController() {
  for (const ctrl of CONTROLLER_REGISTRY.values()) {
    if (ctrl.form) return ctrl;
  }
  return null;
}

function onGlobalDelegatedClick(e) {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const removeBtn = target.closest('[data-remove-filter]');
  const clearBtn = removeBtn ? null : target.closest('[data-clear-all-filters]');
  if (!removeBtn && !clearBtn) return;

  const btn = removeBtn || clearBtn;
  // Only react if the button is inside an active-filter chip strip —
  // keeps the dispatcher from firing on unrelated buttons elsewhere
  // that reuse the same data- attribute name.
  if (!btn.closest('#active-filter-chips')) return;

  const ctrl = findLiveController();
  if (!ctrl) return;

  e.preventDefault();
  if (removeBtn) {
    ctrl.removeFilter(removeBtn.dataset.removeFilter);
  } else {
    ctrl.resetAll();
  }
}

function onGlobalOutsideClick(e) {
  const ctrl = findLiveController();
  if (!ctrl || !ctrl.isOpen()) return;

  const target = e.target;
  if (!(target instanceof Element)) return;

  const panel = ctrl.panel;
  if (panel && panel.contains(target)) return;

  const tb = ctrl.toggleBtn;
  if (tb && tb.contains(target)) return;

  const baseExclusions = '.datepicker, .datepicker-picker, .custom-select-dropdown, .custom-select-wrapper, [data-remove-filter], [data-clear-all-filters]';
  const selectors = ctrl.outsideIgnoreSelectors
    ? `${baseExclusions}, ${ctrl.outsideIgnoreSelectors}`
    : baseExclusions;
  if (target.closest(selectors)) return;

  ctrl.close();
}

function onGlobalKeydown(e) {
  if (e.key !== 'Escape') return;
  const ctrl = findLiveController();
  if (ctrl && ctrl.isOpen()) ctrl.close();
}

function bindDocumentListenersOnce() {
  if (DOC_LISTENERS_BOUND) return;
  DOC_LISTENERS_BOUND = true;
  document.addEventListener('click', onGlobalDelegatedClick);
  document.addEventListener('click', onGlobalOutsideClick);
  document.addEventListener('keydown', onGlobalKeydown);
  // NOTE: re-binding of element listeners after htmx:historyRestore /
  // htmx:afterSwap is handled upstream by ``_runAutoInit`` in app.js,
  // which re-runs the page's ``autoInit…`` function (which in turn
  // calls ``FilterPanelController.init()``). We used to listen for
  // those events here too, but that only made sense while pages
  // still used inline ``<script>`` blocks that did not re-execute
  // on history restore — now every FilterPanelController host is
  // wired through ``data-auto-init`` (see pages/invoices.js,
  // pages/visits.js), so those redundant hooks were dropped.
}


export class FilterPanelController {
  /**
   * @param {object} options
   * @param {string} options.formId                    - filter form id (required)
   * @param {string} [options.panelId='filter-panel']
   * @param {string} [options.toggleBtnId='toggle-filters-btn']
   * @param {string} [options.chevronId='filter-chevron']
   * @param {string} [options.resetBtnId='reset-filters-btn']
   * @param {string} [options.chipStripId='active-filter-chips']
   * @param {string} [options.outsideIgnoreSelectors='']
   *        Extra CSS selectors whose clicks must NOT close the panel
   *        (autocomplete result dropdowns, datepicker popups, …).
   *        Standard exclusions (.datepicker, .custom-select-dropdown,
   *        .custom-select-wrapper, [data-remove-filter],
   *        [data-clear-all-filters]) are always included.
   * @param {Array<{name:string, picker:Function}>} [options.pickers=[]]
   *        SearchPicker bindings. `name` is the hidden input's form
   *        field name; `picker` is a function returning the current
   *        SearchPicker instance (function so the controller reads a
   *        live reference after page re-init, not a stale one).
   * @param {Object<string,string[]>} [options.virtualKeys={}]
   *        Map chip-key → array of form field names to clear. Use for
   *        composite chips like 'date_range': ['date_from','date_to'].
   * @param {Object<string,string>} [options.defaultValues={}]
   *        Map form field name → default value to restore on reset
   *        (e.g. {status: 'active'} on the invoice list). Fields not
   *        listed reset to empty for text/unchecked for boolean.
   */
  constructor(options) {
    this.formId = options.formId;
    this.panelId = options.panelId || 'filter-panel';
    this.toggleBtnId = options.toggleBtnId || 'toggle-filters-btn';
    this.chevronId = options.chevronId || 'filter-chevron';
    this.resetBtnId = options.resetBtnId || 'reset-filters-btn';
    this.chipStripId = options.chipStripId || 'active-filter-chips';
    this.outsideIgnoreSelectors = options.outsideIgnoreSelectors || '';
    this.pickers = options.pickers || [];
    this.virtualKeys = options.virtualKeys || {};
    this.defaultValues = options.defaultValues || {};
    // Field names that Reset must NOT clear — they carry view context, not
    // filters (e.g. ``view`` keeps the active tab, ``job`` the pinned job,
    // ``sort``/``dir`` the current ordering). Without this, resetting the
    // visits "À valider" tab dropped ``view=to_validate`` and bounced the
    // user back to the full "Toutes les visites" list.
    this.preserveFields = new Set(options.preserveFields || []);

    this._onToggleClick = this._onToggleClick.bind(this);
    this._onResetClick = this._onResetClick.bind(this);
  }

  init() {
    // Register BEFORE binding element listeners so the global
    // dispatcher can see this controller if a stray click fires
    // during init (paranoia).
    CONTROLLER_REGISTRY.set(this.formId, this);
    bindDocumentListenersOnce();
    this._bindElementListeners();
  }

  // ------------------------ DOM accessors ------------------------
  get form() { return document.getElementById(this.formId); }
  get panel() { return document.getElementById(this.panelId); }
  get toggleBtn() { return document.getElementById(this.toggleBtnId); }
  get chevron() { return document.getElementById(this.chevronId); }
  get resetBtn() { return document.getElementById(this.resetBtnId); }

  // ------------------------ Open/close --------------------------
  open() {
    const p = this.panel; if (!p) return;
    p.classList.remove('hidden');
    if (this.chevron) this.chevron.classList.add('rotate-180');
  }

  close() {
    const p = this.panel; if (!p) return;
    p.classList.add('hidden');
    if (this.chevron) this.chevron.classList.remove('rotate-180');
  }

  toggle() {
    const p = this.panel; if (!p) return;
    if (p.classList.contains('hidden')) this.open(); else this.close();
  }

  isOpen() {
    const p = this.panel;
    return !!p && !p.classList.contains('hidden');
  }

  // ------------------------ Reset / remove ----------------------
  /** Clear every filter field in the form, then submit. */
  resetAll() {
    const form = this.form; if (!form) return;

    form.querySelectorAll('select').forEach((sel) => this._resetSelect(sel));
    form.querySelectorAll('input').forEach((inp) => this._resetInput(inp));

    // Clear autocomplete pickers without firing their own onClear
    // (we'll fire a single form submit at the end).
    this.pickers.forEach(({ picker }) => {
      const instance = typeof picker === 'function' ? picker() : picker;
      if (!instance) return;
      const savedOnClear = instance.onClear;
      instance.onClear = null;
      try { instance.clear(); } finally { instance.onClear = savedOnClear; }
    });

    this._submit();
  }

  /** Remove one filter by chip key (field name or virtual key), then submit. */
  removeFilter(key) {
    const form = this.form; if (!form) return;

    // 1. Virtual composite keys
    if (this.virtualKeys[key]) {
      this.virtualKeys[key].forEach((fieldName) => this._clearByName(fieldName));
      this._submit();
      return;
    }

    // 2. Picker-driven hidden input
    const pickerEntry = this.pickers.find((p) => p.name === key);
    if (pickerEntry) {
      const instance = typeof pickerEntry.picker === 'function'
        ? pickerEntry.picker() : pickerEntry.picker;
      if (instance) {
        const savedOnClear = instance.onClear;
        instance.onClear = null;
        try { instance.clear(); } finally { instance.onClear = savedOnClear; }
      } else {
        this._clearByName(key);
      }
      this._submit();
      return;
    }

    // 3. Plain form field by name
    this._clearByName(key);
    this._submit();
  }

  // ------------------------ Internals ---------------------------
  _clearByName(name) {
    const form = this.form; if (!form) return;
    const fields = form.querySelectorAll(`[name="${CSS.escape(name)}"]`);
    fields.forEach((el) => {
      if (el.tagName === 'SELECT') {
        this._resetSelect(el);
      } else {
        this._resetInput(el);
      }
    });
  }

  _resetSelect(sel) {
    const name = sel.name;
    if (name && this.preserveFields.has(name)) return; // view/job/sort/dir context
    const target = Object.prototype.hasOwnProperty.call(this.defaultValues, name)
      ? this.defaultValues[name]
      : '';
    // If target isn't one of the options, fall back to the first option
    const hasOption = Array.from(sel.options).some((o) => o.value === target);
    sel.value = hasOption ? target : (sel.options[0] ? sel.options[0].value : '');
    syncCustomSelectLabel(sel);
  }

  _resetInput(inp) {
    const type = (inp.type || '').toLowerCase();
    if (type === 'submit' || type === 'button' || type === 'reset' || type === 'image' || type === 'file') return;

    const name = inp.name;
    if (name && this.preserveFields.has(name)) return; // view/job/sort/dir context
    const hasDefault = name && Object.prototype.hasOwnProperty.call(this.defaultValues, name);

    if (type === 'checkbox' || type === 'radio') {
      if (hasDefault) {
        const def = this.defaultValues[name];
        inp.checked = def === inp.value || def === true || def === '1' || def === 'on';
      } else {
        inp.checked = false;
      }
      return;
    }

    inp.value = hasDefault ? this.defaultValues[name] : '';
  }

  _submit() {
    const form = this.form; if (!form) return;
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  // ------------------------ Binding -----------------------------
  _bindElementListeners() {
    // Per-element listeners must rebind on every init call because
    // hx-boost navigations (and browser back/forward on boosted
    // history) replace these DOM nodes. We track bound nodes in a
    // module-level WeakSet so repeat init on the SAME node is a
    // no-op, while brand-new nodes after a history restore are
    // absent and get their listeners attached. See the BOUND_ELEMENTS
    // comment above for why we can't use a dataset attribute.
    const tb = this.toggleBtn;
    if (tb && !BOUND_ELEMENTS.has(tb)) {
      BOUND_ELEMENTS.add(tb);
      tb.addEventListener('click', this._onToggleClick);
    }

    const rb = this.resetBtn;
    if (rb && !BOUND_ELEMENTS.has(rb)) {
      BOUND_ELEMENTS.add(rb);
      rb.addEventListener('click', this._onResetClick);
    }
  }

  _onToggleClick(e) {
    e.stopPropagation();
    this.toggle();
  }

  _onResetClick(e) {
    e.preventDefault();
    this.resetAll();
  }
}

/**
 * Sync a Flowbite-style custom-select wrapper's visible label to the
 * native <select>'s current selectedOption. Exported for pages that
 * change select.value programmatically outside of the controller
 * (e.g. imperative code in a page module).
 */
export function syncCustomSelectLabel(select) {
  if (!select) return;
  const wrapper = select.closest('.custom-select-wrapper');
  const textEl = wrapper && wrapper.querySelector('.custom-select-text');
  if (!textEl) return;
  const opt = select.options[select.selectedIndex];
  textEl.textContent = opt ? opt.text : '';
}

export default FilterPanelController;
